import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  appleConfirmarCodigo as apiAppleConfirmarCodigo,
  appleCrearCuenta as apiAppleCrearCuenta,
  appleEngancharPorDocumento as apiAppleEnganchar,
  fetchProfile,
  login as apiLogin,
  loginWithApple as apiLoginWithApple,
  loginWithGoogle as apiLoginWithGoogle,
  logout as apiLogout,
  type ActiveContext,
  type RespuestaDeApple,
  type RespuestaDeEnganche,
  type UserProfile,
} from "../api/auth";
import { ApiError, getAccessToken, getViewAsToken } from "../api/client";
import { guardarUltimoEmail } from "../lib/ultimoEmail";
import { borrarPerfilCache, guardarPerfilCache, leerPerfilCache, sujetoDelToken } from "./perfilCache";
import { resetMensajesForja } from "../pages/player/mensajesForja";
import { resetSolicitudesQueEsperan } from "../pages/player/solicitudesQueEsperan";
import { resetAvisos } from "../pages/player/avisos";
import { setSentryUser } from "../observability/sentry";
import { resumePushIfEnabled } from "../push/webPush";

type AuthState =
  | { kind: "unknown" }
  | { kind: "anonymous" }
  | {
      kind: "authenticated";
      profile: UserProfile;
      // null = usuario sin liga todavía (onboarding pendiente).
      activeContext: ActiveContext | null;
      /** Ficha en alguna liga = puede ESCRIBIR en Comunidad (mismo gate que
       *  el 403 del backend). undefined con backend viejo → tratar como true
       *  para no esconderle botones a un jugador por un deploy desfasado. */
      hasPlayerFicha?: boolean;
    }
  | { kind: "error"; message: string };

/** El error es de RED (no del servidor contestando que no). */
function esDeRed(err: unknown): boolean {
  if (err instanceof ApiError) return err.code === "sin_red" || err.status === 0 || err.status >= 500;
  return err instanceof TypeError;
}

type AuthContextValue = {
  state: AuthState;
  login: (email: string, password: string) => Promise<AuthState>;
  loginWithGoogle: (idToken: string) => Promise<AuthState>;
  /**
   * Apple puede no terminar en sesión: cuando no sabemos de quién es la cuenta
   * devuelve `falta_decidir` y hay que preguntar (ver `api/auth.ts`). Por eso
   * esto NO devuelve `AuthState` pelado como los otros dos: lo trae adentro de
   * la rama que sí tiene sesión, y así la pantalla no necesita volver a pedir
   * el perfil para saber a dónde mandar.
   */
  loginWithApple: (
    idToken: string,
    nombre?: string | null,
  ) => Promise<
    | { estado: "listo"; sesion: AuthState }
    | Extract<RespuestaDeApple, { estado: "falta_decidir" }>
  >;
  /** Segundo paso de Apple: "ya tengo cuenta". El documento sólo dice a qué
   *  mail mandar el código; NO hay sesión todavía. */
  appleEnganchar: (vale: string, documento: string) => Promise<RespuestaDeEnganche>;
  /** Tercer paso: el código del mail es la prueba. Acá sí hay sesión. */
  appleConfirmarCodigo: (vale: string, codigo: string) => Promise<AuthState>;
  /** Segundo paso de Apple: "soy nuevo". Recién acá se crea la cuenta. */
  appleCrearCuenta: (vale: string, nombre?: string | null) => Promise<AuthState>;
  logout: () => Promise<void>;
  refresh: () => Promise<AuthState>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ kind: "unknown" });

  const refresh = useCallback(async () => {
    if (!getAccessToken()) {
      borrarPerfilCache();
      const nextState: AuthState = { kind: "anonymous" };
      setState(nextState);
      return nextState;
    }
    try {
      const res = await fetchProfile();
      const nextState: AuthState = {
        kind: "authenticated",
        profile: res.user,
        activeContext: res.activeContext,
        hasPlayerFicha: res.hasPlayerFicha,
      };
      guardarPerfilCache(res, sujetoDelToken(getAccessToken()));
      // El mail que precarga la pantalla de ingreso de la PWA. El VER COMO del
      // superusuario no cuenta: es la cuenta de otro.
      if (!getViewAsToken()) guardarUltimoEmail(res.user.email);
      setState(nextState);
      return nextState;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        borrarPerfilCache();
        const nextState: AuthState = { kind: "anonymous" };
        setState(nextState);
        return nextState;
      }
      // SIN SEÑAL NO SE PIERDE LA SESIÓN. El árbitro que recarga en la cancha
      // sin datos veía la pantalla de INGRESAR en pleno partido (los tokens y la
      // cola seguian ahi, pero el perfil no se podia leer y la ruta protegida
      // mandaba al login). Con el último perfil conocido entra igual y la app
      // sigue trabajando contra su cola y su caché.
      // La foto vale sólo si es de ESTE usuario (ver perfilCache.ts).
      const cache = leerPerfilCache(sujetoDelToken(getAccessToken()));
      if (cache && esDeRed(err)) {
        const nextState: AuthState = {
          kind: "authenticated",
          profile: cache.user,
          activeContext: cache.activeContext,
          hasPlayerFicha: cache.hasPlayerFicha,
        };
        setState(nextState);
        return nextState;
      }
      const message = err instanceof Error ? err.message : String(err);
      const nextState: AuthState = { kind: "error", message };
      setState(nextState);
      return nextState;
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Etiquetar los incidentes de Sentry con quién los sufrió. Sin esto un error
  // dice "algo falló" pero no a quién llamar para preguntarle qué estaba
  // haciendo — que en una liga de 10 personas es la mitad del diagnóstico.
  useEffect(() => {
    setSentryUser(
      state.kind === "authenticated" ? { id: state.profile.id, email: state.profile.email } : null,
    );
  }, [state]);

  // Push ya activada en este dispositivo: al abrir con sesión, re-registrar el
  // token (Firebase lo rota) y enganchar los avisos en primer plano. Sin
  // sesión no hace nada; sin soporte del navegador tampoco. Nunca prompta.
  useEffect(() => {
    if (state.kind === "authenticated") void resumePushIfEnabled();
  }, [state.kind]);

  useEffect(() => {
    const handleAuthExpired = () => {
      // La sesión murió de verdad (refresh vencido o revocado desde otro
      // dispositivo): la foto del perfil no tiene por qué quedar en el disco.
      borrarPerfilCache();
      setState({ kind: "anonymous" });
    };
    const handleStorage = (event: StorageEvent) => {
      if (event.key === "forja.access_token" && !event.newValue) {
        handleAuthExpired();
      }
    };

    window.addEventListener("forja:auth-expired", handleAuthExpired);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("forja:auth-expired", handleAuthExpired);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      await apiLogin({ email, password });
      return refresh();
    },
    [refresh],
  );

  const loginWithGoogle = useCallback(
    async (idToken: string) => {
      await apiLoginWithGoogle(idToken);
      return refresh();
    },
    [refresh],
  );

  // Ojo: si Apple devuelve "falta_decidir" NO hay sesión todavía, así que acá
  // no se refresca el perfil. Refrescar con el token viejo (o sin token) es
  // justo lo que dejaría la pantalla a medio camino entre anónimo y logueado.
  const loginWithApple = useCallback(
    async (idToken: string, nombre?: string | null) => {
      const res = await apiLoginWithApple(idToken, nombre);
      if (res.estado === "listo") return { estado: "listo" as const, sesion: await refresh() };
      return res;
    },
    [refresh],
  );

  // No refresca: después del documento todavía no hay sesión, sólo un código
  // en camino. Refrescar acá dejaría la pantalla entre anónimo y logueado.
  const appleEnganchar = useCallback(
    (vale: string, documento: string) => apiAppleEnganchar(vale, documento),
    [],
  );

  const appleConfirmarCodigo = useCallback(
    async (vale: string, codigo: string) => {
      await apiAppleConfirmarCodigo(vale, codigo);
      return refresh();
    },
    [refresh],
  );

  const appleCrearCuenta = useCallback(
    async (vale: string, nombre?: string | null) => {
      await apiAppleCrearCuenta(vale, nombre);
      return refresh();
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    borrarPerfilCache();
    // Que la próxima cuenta en este navegador no herede el contador.
    resetMensajesForja();
    resetSolicitudesQueEsperan();
    resetAvisos();
    await apiLogout();
    setState({ kind: "anonymous" });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      login,
      loginWithGoogle,
      loginWithApple,
      appleEnganchar,
      appleConfirmarCodigo,
      appleCrearCuenta,
      logout,
      refresh,
    }),
    [
      state,
      login,
      loginWithGoogle,
      loginWithApple,
      appleEnganchar,
      appleConfirmarCodigo,
      appleCrearCuenta,
      logout,
      refresh,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
